import { Box, Typography, useTheme, Card, CardContent, CircularProgress, Grid, FormControlLabel, Checkbox, Alert, Button, Dialog, DialogTitle, DialogContent, IconButton, Divider, FormGroup, TextField, InputAdornment, Tooltip, Chip } from "@mui/material";
import { tokens } from "../../../theme";
import CloseIcon from '@mui/icons-material/Close';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TagIcon from '@mui/icons-material/Tag';
import AbcIcon from '@mui/icons-material/Abc';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StorageIcon from '@mui/icons-material/Storage';
import ListAltIcon from '@mui/icons-material/ListAlt';
import BarChartIcon from '@mui/icons-material/BarChart';

import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip as ChartTooltip, Legend, BarElement } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useState, useEffect, useMemo } from 'react';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend
);

// Helper to infer type for auto-selection & icons
const inferFeatureType = (name) => {
    const lower = name.toLowerCase();
    if (lower.includes('age') || lower.includes('year') || lower.includes('count') || lower.includes('days') || lower.includes('id') || lower.includes('samples') || lower.includes('value')) return 'NUM';
    if (lower.includes('percent') || lower.includes('ratio') || lower.includes('rate') || lower.includes('mean') || lower.includes('std')) return 'NUM';
    if (lower.includes('is_') || lower.includes('has_') || lower.includes('grade') || lower.includes('stage') || lower.includes('status') || lower.includes('type') || lower.includes('class') || lower.includes('gender')) return 'CAT';
    return 'NUM'; // Default to NUM for unknown to encourage plotting
};

// Helper to format keys like "tumour_presence_confirmed" -> "Tumour Presence Confirmed"
const formatTitle = (str) => {
    if (!str) return '';
    return str.split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

const VisualizationModal = ({ open, onClose, dataset, isGenerating, onGenerate, displayableFeatures, displayableFeaturesLoading, selectedFeatures, setSelectedFeatures, onFeatureToggle, onSelectAll, visualizations }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [searchTerm, setSearchTerm] = useState("");
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  
  // Focus Modal State
  const [focusViz, setFocusViz] = useState(null);

  // Filter features
  const filteredFeatures = useMemo(() => {
     if (!searchTerm) return displayableFeatures;
     return displayableFeatures.filter(f => f.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [displayableFeatures, searchTerm]);

  // Auto-Select Logic
  useEffect(() => {
      if (open && displayableFeatures.length > 0 && selectedFeatures.length === 0 && !hasAutoSelected) {
          const numerical = displayableFeatures.filter(f => inferFeatureType(f) === 'NUM');
          const toSelect = numerical.slice(0, 6);
          if (toSelect.length > 0) {
              setSelectedFeatures(toSelect);
              setHasAutoSelected(true);
          }
      }
      if (!open) setHasAutoSelected(false);
  }, [open, displayableFeatures, selectedFeatures.length, setSelectedFeatures, hasAutoSelected]);

  return (
    <>
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: colors.grey[900],
          backgroundImage: 'none',
          border: `1px solid ${colors.grey[800]}`,
          borderRadius: 2,
          height: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: `1px solid ${colors.grey[800]}`, p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <Box>
           <Typography variant="h4" sx={{ color: colors.grey[100], fontWeight: 700 }}>
             Feature Visualization
           </Typography>
           <Typography variant="body2" sx={{ color: colors.grey[400], mt: 0.5 }}>
             Analyze dataset distribution for <strong>{dataset?.name}</strong>
           </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: colors.grey[400] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, overflow: 'hidden', flex: 1 }}>
        {/* Left Panel: Feature Selection - FIXED SIDEBAR layout */}
        <Box sx={{ 
          width: { xs: '100%', md: '320px' }, 
          borderRight: { md: `1px solid ${colors.grey[800]}` }, 
          borderBottom: { xs: `1px solid ${colors.grey[800]}`, md: 'none' },
          bgcolor: 'var(--fx-paper)',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          flexShrink: 0
        }}>
           {/* Sidebar Header: Search & Select All */}
           <Box sx={{ p: 2, pb: 2, flexShrink: 0 }}>
               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                   <Typography variant="subtitle1" sx={{ color: colors.grey[100], fontWeight: 600 }}>
                     Select Features
                   </Typography>
                   {selectedFeatures.length > 0 && (
                       <Button 
                           size="small" 
                           onClick={() => setSelectedFeatures([])}
                           startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                           sx={{ 
                               textTransform: 'none', 
                               color: colors.grey[400], 
                               fontSize: '0.75rem',
                               minWidth: 'auto',
                               p: 0.5,
                               '&:hover': { color: '#ef4444', bgcolor: 'transparent' }
                           }}
                       >
                           Clear
                       </Button>
                   )}
               </Box>
               
               <TextField 
                  fullWidth
                  placeholder="Search variables..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ mb: 2, borderRadius: 1 }}
                  InputProps={{
                      startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: colors.grey[400], fontSize: 18 }}/></InputAdornment>
                  }}
               />
               
               <FormControlLabel
                  control={
                    <Checkbox
                      checked={filteredFeatures.length > 0 && filteredFeatures.every(f => selectedFeatures.includes(f))}
                      indeterminate={filteredFeatures.some(f => selectedFeatures.includes(f)) && !filteredFeatures.every(f => selectedFeatures.includes(f))}
                      onChange={(e) => {
                          if (e.target.checked) {
                              const combined = new Set([...selectedFeatures, ...filteredFeatures]);
                              setSelectedFeatures([...combined]);
                          } else {
                              setSelectedFeatures(selectedFeatures.filter(f => !filteredFeatures.includes(f)));
                          }
                      }}
                      sx={{ color: colors.secondary[500], p: 0.5, mr: 1, '&.Mui-checked': { color: colors.secondary[500] } }}
                    />
                  }
                  label={<Typography variant="body2" sx={{ color: colors.grey[200], fontWeight: 600, fontSize: '0.85rem' }}>Select All Visible</Typography>}
                  sx={{ m: 0, ml: 0.5 }}
               />
           </Box>
           
           <Divider sx={{ borderColor: colors.grey[800] }} />

           {/* Sidebar List: Scrollable Area */}
           <Box sx={{ 
               flexGrow: 1, 
               overflowY: 'auto', 
               p: 2, 
               pt: 1,
               // Custom Scrollbar
               '&::-webkit-scrollbar': { width: '6px' },
               '&::-webkit-scrollbar-track': { background: 'transparent' },
               '&::-webkit-scrollbar-thumb': { 
                   backgroundColor: 'var(--fx-border-strong)', // Slate-300
                   borderRadius: '3px',
               },
               '&::-webkit-scrollbar-thumb:hover': { 
                   backgroundColor: 'var(--fx-text-muted)' // Slate-400
               }
           }}>
               {displayableFeaturesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={30} color="secondary" />
                  </Box>
               ) : filteredFeatures.length > 0 ? (
                    <FormGroup>
                      {filteredFeatures.map(feature => {
                          const type = inferFeatureType(feature);
                          const isSelected = selectedFeatures.includes(feature);
                          return (
                            <Box key={feature} sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                mb: 0.5, 
                                bgcolor: isSelected ? 'var(--fx-info-soft)' : 'transparent',
                                borderLeft: isSelected ? '4px solid #06b6d4' : '4px solid transparent', // Cyan border
                                borderRadius: isSelected ? '0 4px 4px 0' : 1,
                                transition: 'all 0.2s',
                                '&:hover': { bgcolor: isSelected ? 'var(--fx-info-soft)' : 'var(--fx-subtle)' } 
                            }}>
                                <Checkbox
                                  checked={isSelected}
                                  onChange={(e) => onFeatureToggle(feature, e.target.checked)}
                                  size="small"
                                  sx={{ 
                                      color: colors.grey[600], 
                                      p: 0.8, 
                                      '&.Mui-checked': { color: '#06b6d4' } 
                                  }}
                                />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden', flex: 1, py: 0.5 }}>
                                    <Box sx={{ 
                                        display: 'flex', alignItems: 'center', justifyItems: 'center', 
                                        bgcolor: type === 'NUM' ? 'var(--fx-info-soft)' : 'var(--fx-warning-soft)',
                                        color: type === 'NUM' ? 'var(--fx-info-text)' : 'var(--fx-warning-text)',
                                        borderRadius: '4px',
                                        p: '2px',
                                        width: 20, height: 20,
                                        flexShrink: 0
                                    }}>
                                        {type === 'NUM' ? <TagIcon sx={{ fontSize: 14, m: 'auto' }} /> : <AbcIcon sx={{ fontSize: 16, m: 'auto' }} />}
                                    </Box>
                                    <Typography variant="body2" sx={{ 
                                        color: isSelected ? '#1e293b' : colors.grey[300], // Dark Slate if active
                                        fontWeight: isSelected ? 600 : 400,
                                        fontSize: '0.85rem', 
                                        whiteSpace: 'nowrap', 
                                        overflow: 'hidden', 
                                        textOverflow: 'ellipsis' 
                                    }}>
                                        {formatTitle(feature)}
                                    </Typography>
                                </Box>
                            </Box>
                          );
                      })}
                    </FormGroup>
               ) : (
                 <Alert severity="info" sx={{ bgcolor: 'transparent', color: colors.grey[400] }}>
                    {displayableFeatures.length === 0 ? "No features available" : "No matches found"}
                 </Alert>
               )}
           </Box>

           {/* Sidebar Footer: Sticky Action Button */}
           <Box sx={{ p: 2, borderTop: `1px solid ${colors.grey[800]}`, bgcolor: colors.grey[900], flexShrink: 0 }}>
             <Button
                variant="contained"
                fullWidth
                onClick={onGenerate}
                disabled={selectedFeatures.length === 0 || isGenerating}
                startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <AnalyticsIcon />}
                sx={{
                  bgcolor: colors.secondary[500],
                  color: 'white',
                  py: 1.2,
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  textTransform: 'none',
                  borderRadius: 2,
                  '&:hover': { bgcolor: colors.secondary[600] },
                  '&:disabled': { bgcolor: colors.grey[700], color: colors.grey[500] }
                }}
             >
               {isGenerating ? 'Generating...' : `Visualize (${selectedFeatures.length})`}
             </Button>
           </Box>
        </Box>

        {/* Right Panel: Visualizations - Scrollable Canvas */}
        <Box sx={{ flex: 1, p: 3, overflowY: 'auto', bgcolor: 'var(--fx-subtle)' }}>
          {visualizations ? (
            <Box>
               {/* Stats Summary */}
               <Grid container spacing={2} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ 
                        bgcolor: 'var(--fx-paper)', 
                        p: 2, 
                        border: '1px solid var(--fx-border)', 
                        borderLeft: '4px solid #06b6d4', // Cyan Border
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', 
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                    }}>
                       <Box sx={{ p: 1.5, bgcolor: 'var(--fx-info-soft)', borderRadius: '50%', color: 'var(--fx-secondary)' }}>
                           <StorageIcon sx={{ fontSize: 24 }} />
                       </Box>
                       <Box>
                           <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b', fontSize: '1.875rem', lineHeight: 1 }}>
                             {visualizations.num_sample?.toLocaleString() || '-'}
                           </Typography>
                           <Typography variant="caption" sx={{ color: 'var(--fx-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                               Total Samples
                           </Typography>
                       </Box>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ 
                        bgcolor: 'var(--fx-paper)', 
                        p: 2, 
                        border: '1px solid var(--fx-border)', 
                        borderLeft: '4px solid #8b5cf6', // Violet Border
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', 
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                    }}>
                       <Box sx={{ p: 1.5, bgcolor: '#f5f3ff', borderRadius: '50%', color: '#8b5cf6' }}>
                           <ListAltIcon sx={{ fontSize: 24 }} />
                       </Box>
                       <Box>
                           <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b', fontSize: '1.875rem', lineHeight: 1 }}>
                             {visualizations.num_feature || '-'}
                           </Typography>
                           <Typography variant="caption" sx={{ color: 'var(--fx-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                               Total Features
                           </Typography>
                       </Box>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ 
                        bgcolor: 'var(--fx-paper)', 
                        p: 2, 
                        border: '1px solid var(--fx-border)', 
                        borderLeft: '4px solid #f59e0b', // Amber Border
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', 
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                    }}>
                       <Box sx={{ p: 1.5, bgcolor: 'var(--fx-warning-soft)', borderRadius: '50%', color: 'var(--fx-warning)' }}>
                           <BarChartIcon sx={{ fontSize: 24 }} />
                       </Box>
                       <Box>
                           <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b', fontSize: '1.875rem', lineHeight: 1 }}>
                             {visualizations.visualizations?.length || 0}
                           </Typography>
                           <Typography variant="caption" sx={{ color: 'var(--fx-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                               Charts Generated
                           </Typography>
                       </Box>
                    </Card>
                  </Grid>
               </Grid>

               {/* Charts Grid */}
               <Grid container spacing={3} sx={{ pb: 4 }}>
                 {visualizations.visualizations?.map((viz, idx) => {
                   // Correct Empty State Check: Check if data exists AND sum of counts > 0
                   const hasData = viz.data && viz.data.counts && viz.data.counts.length > 0 && viz.data.counts.reduce((a, b) => a + b, 0) > 0;
                   const type = inferFeatureType(viz.feature_name);

                   // DEBUG LOG
                   console.log(`[VizModal] Rendering ${viz.feature_name} (${viz.chart_type})`, viz);

                   return (
                   <Grid item xs={12} xl={6} key={idx}>
                     <Card sx={{ 
                         bgcolor: 'var(--fx-paper)', 
                         height: '100%', 
                         border: '1px solid var(--fx-border)', 
                         boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', 
                         borderRadius: 2,
                         overflow: 'hidden',
                         display: 'flex',
                         flexDirection: 'column'
                     }}>
                       {/* Chart Header Toolbar */}
                       <Box sx={{ 
                           p: 1.5, 
                           px: 2, 
                           bgcolor: 'var(--fx-subtle)', 
                           borderBottom: '1px solid var(--fx-subtle)',
                           display: 'flex',
                           justifyContent: 'space-between',
                           alignItems: 'center'
                       }}>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                               <Typography variant="subtitle2" sx={{ color: 'var(--fx-text-strong)', fontWeight: 700 }}>
                                    {formatTitle(viz.feature_name)}
                               </Typography>
                               <Chip 
                                   label={type} 
                                   size="small" 
                                   sx={{ 
                                       height: 20, 
                                       fontSize: '0.65rem', 
                                       fontWeight: 700,
                                       bgcolor: type === 'NUM' ? 'var(--fx-subtle)' : 'var(--fx-warning-soft)',
                                       color: type === 'NUM' ? 'var(--fx-text-secondary)' : '#d97706',   // Slate-600 or Amber-600
                                       borderRadius: '999px', // Pill shape
                                       border: type === 'NUM' ? '1px solid var(--fx-border-strong)' : '1px solid #fcd34d',
                                       px: 0.5
                                   }} 
                               />
                           </Box>
                           <Box sx={{ display: 'flex', gap: 0.5 }}>
                               <Tooltip title={`Raw Data Info: Type=${type}, Bins=${viz.data?.bins?.length || 0}`}>
                                   <IconButton size="small" sx={{ color: 'var(--fx-text-muted)', p: 0.5, '&:hover': { color: 'var(--fx-text-secondary)' } }}>
                                      <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                                   </IconButton>
                               </Tooltip>
                               {hasData && (
                                   <Tooltip title="Expand Chart">
                                       <IconButton 
                                           size="small" 
                                           onClick={() => setFocusViz(viz)}
                                           sx={{ color: 'var(--fx-text-muted)', p: 0.5, '&:hover': { color: '#06b6d4' } }}
                                       >
                                          <OpenInFullIcon sx={{ fontSize: 16 }} />
                                       </IconButton>
                                   </Tooltip>
                               )}
                           </Box>
                       </Box>

                       {/* Chart Content Area */}
                       <Box sx={{ p: 2, flexGrow: 1, height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                         {!hasData ? (
                             <Box sx={{ 
                                 textAlign: 'center', 
                                 opacity: 0.6, 
                                 p: 4, 
                                 border: '2px dashed var(--fx-border-strong)', 
                                 borderRadius: 2, 
                                 width: '100%',
                                 bgcolor: 'var(--fx-subtle)'
                             }}>
                                 <Box sx={{ position: 'relative', display: 'inline-block', mb: 1 }}>
                                     <AnalyticsIcon sx={{ fontSize: 32, color: 'var(--fx-text-muted)' }} />
                                     <Box sx={{ 
                                         position: 'absolute', 
                                         top: '50%', left: '50%', 
                                         width: '120%', height: '2px', 
                                         bgcolor: 'var(--fx-text-muted)', 
                                         transform: 'translate(-50%, -50%) rotate(-45deg)',
                                         borderRadius: 1
                                     }} />
                                 </Box>
                                 <Typography variant="body2" sx={{ color: 'var(--fx-text-secondary)', fontWeight: 500 }}>
                                     No distribution data available
                                 </Typography>
                                 <Typography variant="caption" sx={{ color: 'var(--fx-text-muted)' }}>
                                     Values may be null or constant
                                 </Typography>
                             </Box>
                         ) : (viz.chart_type === 'histogram' || viz.chart_type === 'bar') && (
                           <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
                           <Bar
                             data={{
                               labels: viz.data.bins || viz.data.categories,
                               datasets: [{
                                 label: 'Count',
                                 data: viz.data.counts,
                                 borderRadius: 4,
                                 barPercentage: 0.8,
                                 categoryPercentage: 0.9,
                                 // Contextual Gradient
                                 backgroundColor: (context) => {
                                      const ctx = context.chart.ctx;
                                      const gradient = ctx.createLinearGradient(0, 0, 0, 300);
                                      gradient.addColorStop(0, 'rgba(6, 182, 212, 0.8)'); // Cyan-500
                                      gradient.addColorStop(1, 'rgba(6, 182, 212, 0.1)'); // Transparent
                                      return gradient;
                                 },
                                 hoverBackgroundColor: '#0891b2'
                               }]
                             }}
                             options={{
                               responsive: true,
                               maintainAspectRatio: false,
                               animation: { duration: 500 },
                               plugins: { 
                                   legend: { display: false },
                                   tooltip: { 
                                       backgroundColor: '#1e293b', // Dark Slate
                                       padding: 12,
                                       cornerRadius: 6,
                                       titleFont: { size: 13, weight: 600 },
                                       bodyFont: { size: 12 },
                                       displayColors: false,
                                       callbacks: {
                                           title: (items) => `${formatTitle(viz.feature_name)}: ${items[0].label}`
                                       }
                                   }
                               },
                               scales: {
                                 x: { 
                                     grid: { display: false }, 
                                     ticks: { color: 'var(--fx-text-muted)', font: { size: 10 }, maxRotation: 45, minRotation: 0 }
                                 },
                                 y: { 
                                     border: { display: false },
                                     grid: { color: 'var(--fx-border)', borderDash: [5, 5] }, 
                                     ticks: { color: 'var(--fx-text-muted)', font: { size: 10 } },
                                     beginAtZero: true
                                 }
                               }
                             }}
                           />
                           </Box>
                         )}
                       </Box>
                     </Card>
                   </Grid>
                 )})}
               </Grid>
            </Box>
          ) : (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', opacity: 0.6 }}>
               <AnalyticsIcon sx={{ fontSize: 80, color: 'var(--fx-text-muted)', mb: 3 }} />
               <Typography variant="h5" sx={{ color: 'var(--fx-text-secondary)', fontWeight: 700, mb: 1 }}>
                 Ready to Visualize
               </Typography>
               <Typography variant="body1" sx={{ color: 'var(--fx-text-muted)' }}>
                 Select features from the left sidebar and click "Visualize"
               </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>

    {/* Focus Modal for Main Chart View */}
    <Dialog 
        open={Boolean(focusViz)} 
        onClose={() => setFocusViz(null)}
        maxWidth="lg"
        fullWidth
    >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--fx-border)' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatTitle(focusViz?.feature_name)} Distribution</Typography>
            <IconButton onClick={() => setFocusViz(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ minHeight: 500, p: 4 }}>
             {focusViz && (
               <Bar
                 data={{
                   labels: focusViz.data.bins || focusViz.data.categories,
                   datasets: [{
                     label: 'Count',
                     data: focusViz.data.counts,
                     borderRadius: 4,
                     barPercentage: 0.8,
                     categoryPercentage: 0.9,
                     backgroundColor: (context) => {
                          const ctx = context.chart.ctx;
                          const gradient = ctx.createLinearGradient(0, 0, 0, 400);
                          gradient.addColorStop(0, 'rgba(6, 182, 212, 0.8)'); 
                          gradient.addColorStop(1, 'rgba(6, 182, 212, 0.1)');
                          return gradient;
                     },
                   }]
                 }}
                 options={{
                   responsive: true,
                   maintainAspectRatio: false,
                   plugins: { 
                       legend: { display: false },
                       tooltip: { 
                           backgroundColor: '#1e293b',
                           padding: 12,
                           cornerRadius: 6,
                           titleFont: { size: 14 },
                           bodyFont: { size: 13 },
                           displayColors: false
                       }
                   },
                   scales: {
                     x: { 
                         grid: { display: false },
                         ticks: { color: 'var(--fx-text-muted)', font: { size: 11 } }
                     },
                     y: { 
                         border: { display: false },
                         grid: { color: 'var(--fx-border)', borderDash: [5, 5] }, 
                         ticks: { color: 'var(--fx-text-muted)', font: { size: 11 } },
                         beginAtZero: true 
                     }
                   }
                 }}
               />
             )}
        </DialogContent>
    </Dialog>
    </>
  );
};
export default VisualizationModal;
